import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, TextField } from "@mui/material";
import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { CloseIcon } from "../../../minimals/theme/overrides/CustomIcons";
import { UserContext } from "../../contexts/UserContext";
import InternalUserAutocomplete from "../../shared/InternalUserAutocomplete";
import { InternalUser } from "../../types/app";
import { fCurrency } from "../../utils/formatNumber";
import NotesTimeline from "../../checks/checkNotes/NotesTimeline";
import { prependUserToSelectNote } from "../../utils/prependUserToSelectNote";
import { processedNotes } from "../../utils/processNotes";
import { Note, SearchNotOrdered } from "../types";
import { buildTempNote } from "./buildTempNote";

interface IProps {
  entity: SearchNotOrdered;
  setEntity: Function;
  setEntityTable?: Function;
  isOpen: boolean;
  orderNumberDisplay?: string;
  amount?: string;
  priority?: string;
  nameBeforeUpdate: string;
  emailBeforeUpdate: string;
  refetchData?: Function;
}

export default function NotesModal({
  entity,
  setEntity,
  setEntityTable,
  orderNumberDisplay,
  amount,
  isOpen,
  nameBeforeUpdate = "",
  emailBeforeUpdate = ""
}: IProps) {
  const [submitting, setSubmitting] = useState(false);
  const { user } = useContext(UserContext);
  const [text, setText] = useState<string>("");
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<InternalUser | null>({
    fullName: nameBeforeUpdate || "",
    email: emailBeforeUpdate || ""
  } as InternalUser);

  const fetchAndSetAssignedToUser = async (selectedAssignedToEmail: string) => {
    if (!selectedAssignedToEmail) return;
    const { data } = await axios.get(
      `/api/clientphonebook/internalusers/getInternalUserByEmail?email=${selectedAssignedToEmail}`
    );
    if (data) {
      setSelectedAssignedTo(data);
    }
  };

  const saveAssignTo = async (assignToUser: InternalUser | null) => {
    if (!assignToUser || assignToUser.email?.toLowerCase() == emailBeforeUpdate?.toLowerCase()) return;
    await axios.post("/proxy/api/requestedtasks/update", {
      orderNumber: entity.orderNumber,
      task: {
        description: "County Search",
        assignedToUsername: assignToUser.username.replace("WORKGROUP\\", "")
      }
    });
  };

  const handleSaveNote = async (text: string) => {
    if (!user) return;
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber: entity.orderNumber,
      taskDescription: "County Search",
      text: prependUserToSelectNote(text, user.name)
    });
  };

  const save = async () => {
    setSubmitting(true);
    const localNotes: Note[] = [];
    if (nameBeforeUpdate !== selectedAssignedTo?.fullName) {
      const assignedText = `Assigned to ${selectedAssignedTo!.fullName}`;
      await saveAssignTo(selectedAssignedTo).then(async () => {
        await handleSaveNote(assignedText);
        localNotes.push(buildTempNote(assignedText, entity, user?.name!!));
      });
    }
    if (text) {
      await handleSaveNote(text);
      localNotes.push(buildTempNote(text, entity, user?.name!!));
    }
    const updatedEntity = {
      ...entity,
      notes: [...entity.notes, ...localNotes],
      assignedToUser: selectedAssignedTo,
      assignedToEmail: selectedAssignedTo?.email,
      assignedToName: selectedAssignedTo?.fullName,
      assignedTo: selectedAssignedTo?.fullName
    };
    setEntity(updatedEntity);

    if (setEntityTable) {
      setEntityTable((state: SearchNotOrdered[]) =>
        state.map((s) => (s.orderNumberWithPrefix === entity.orderNumberWithPrefix ? updatedEntity : s))
      );
    }

    if (text) setText("");
    setSubmitting(false);
  };

  useEffect(() => {
    fetchAndSetAssignedToUser(selectedAssignedTo?.email || "");
  }, []);

  const onClose = () => setEntity(null);

  const notes = useMemo(() => {
    return processedNotes(entity.notes);
  }, [entity.notes]);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" marginBottom={3}>
          <Box>
            Order Number {orderNumberDisplay || entity.orderNumber}
            {amount ? ` - ${fCurrency(amount)}` : ""}
          </Box>
        </Stack>
        <InternalUserAutocomplete
          sx={{ width: 220 }}
          value={selectedAssignedTo}
          onChange={(val) => {
            setSelectedAssignedTo(val as InternalUser | null);
          }}
          label="Assigned to"
          limitToAccountReps
        />
      </DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ paddingBottom: 3 }}>
        <Box paddingTop={1}>
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            rows={3}
            label="Note"
            sx={{ marginBottom: 2 }}
          />
          <Stack direction="row" justifyContent="right">
            <Button variant="contained" size="small" onClick={save} disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
        <NotesTimeline notes={notes} />
      </DialogContent>
    </Dialog>
  );
}
